/** @typedef {import('./BalancedNode').BalancedNode} BalancedNode */

/**
 * Pure helpers to mutate the `columnKey → nodeId[]` arrangement for group moves
 * (HU-04/09). They never mutate their input.
 *
 * @module balancedColumns
 */

/**
 * Story 146 / S66 (RN30.1, D30) — whether a node is a JOB marked as external.
 *
 * THE single decision point of the story in this editor: every gate through which a node could
 * enter the matrix (the job tree, the columns rebuilt from the loaded nodes, the remap, a drop and
 * the save body) asks this and nothing else.
 *
 * The flag is `extern` because it comes from the NODE (`job_reference.extern`), not from a device;
 * `external` is the machine's mark and is deliberately not read here (03-dominio.md §5.8, RN64).
 * Absent or false means internal, so nodes served by a Sun that predates the flag keep behaving
 * exactly as today (RN64.2).
 *
 * @param {BalancedNode} node
 * @returns {boolean}
 */
export const isExternalJobNode = (node) => node?.nodeType === 'JOB' && node?.jobReference?.extern === true

/**
 * The nodes that may take part in the matrix: everything except the external JOB nodes. Group
 * (`WORKFLOW`) nodes always survive — they define the tree, they are never placed.
 *
 * ⚠️ This is a projection FOR THE MATRIX, never a replacement of `balancedData.nodes`: the external
 * node stays in the state so the line-advancement graph (`previousNodes`/`nextNodes`) keeps its
 * shape and the internal nodes around it keep exactly the range they have today (S66, last step).
 *
 * @param {BalancedNode[]} nodes
 * @returns {BalancedNode[]}
 */
export const placeableJobNodes = (nodes) => (nodes ?? []).filter(node => !isExternalJobNode(node))

/**
 * Returns a copy of `columnNodeIds` with every id in `ids` removed from all columns.
 *
 * @param {Object<string, string[]>} columnNodeIds
 * @param {string[]} ids
 * @returns {Object<string, string[]>}
 */
export const removeNodesFromColumns = (columnNodeIds, ids) => {
  const remove = new Set(ids)
  const result = {}
  for (const [key, list] of Object.entries(columnNodeIds ?? {})) {
    result[key] = (list ?? []).filter(id => !remove.has(id))
  }
  return result
}

/**
 * Returns a copy of `columnNodeIds` with `ids` moved to the end of `targetKey`,
 * removing them from any column they were in first (so a relocation never
 * duplicates an id). Ids keep the given order.
 *
 * @param {Object<string, string[]>} columnNodeIds
 * @param {string[]} ids
 * @param {string} targetKey
 * @returns {Object<string, string[]>}
 */
export const appendNodesToColumn = (columnNodeIds, ids, targetKey) => {
  const cleaned = removeNodesFromColumns(columnNodeIds, ids)
  cleaned[targetKey] = [...(cleaned[targetKey] ?? []), ...ids]
  return cleaned
}

/**
 * Whether every node's job technology is supported by `device` (LINE mode). Used
 * for the all-or-nothing group drop: if any node fails, the whole group is
 * rejected (S13). A null/undefined device imposes no restriction.
 *
 * @param {import('./BalancedNode').BalancedNode[]} nodes
 * @param {{ technologies?: {id: number}[] }|null|undefined} device
 * @returns {boolean}
 */
export const allNodesSupportedByDevice = (nodes, device) => {
  if (!device) return true
  const techIds = new Set((device.technologies ?? []).map(t => t.id))
  return (nodes ?? []).every(n => techIds.has(n?.jobReference?.technology?.id))
}

/**
 * Toggles `nodeId` in the multi-selection. When the selection is empty, the
 * currently info-selected node (`seedId`) is seeded in first so it joins the
 * group to move; a seed equal to `nodeId` is not double-counted.
 *
 * @param {Set<string>} current - Current multi-selection
 * @param {string} nodeId - Node toggled by the Ctrl/Cmd+click
 * @param {string|null|undefined} seedId - Currently info-selected node id, if any
 * @returns {Set<string>} A new Set
 */
export const toggleMultiSelection = (current, nodeId, seedId) => {
  const next = new Set(current)
  if (next.size === 0 && seedId != null && seedId !== nodeId) next.add(seedId)
  if (next.has(nodeId)) next.delete(nodeId)
  else next.add(nodeId)
  return next
}
